import React, { useState } from "react";
import { CloudUpload, X } from "lucide-react";
import { helpService } from "@/services/help";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const Help: React.FC = () => {
  const [requestType, setRequestType] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.size > 5 * 1024 * 1024) {
      setError("Photo must be under 5MB");
      return;
    }
    setPhoto(file || null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setLoading(true);

    try {
      await helpService.submitHelpRequest({
        requestType,
        title,
        description,
        photo: photo || undefined,
      });
      setSuccess(true);
      setRequestType("");
      setTitle("");
      setDescription("");
      setPhoto(null);
    } catch {
      setError("Failed to submit help request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <h1 className="mb-6 w-full max-w-xl text-3xl font-semibold tracking-tight">Help & Support</h1>

      <Card className="w-full max-w-xl">
        <CardContent className="p-6 pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {success && (
              <Alert className="border-green-600/50 bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-100">
                <AlertDescription>Help request submitted successfully!</AlertDescription>
              </Alert>
            )}
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label>Request Type</Label>
              <Select value={requestType} onValueChange={setRequestType} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="feature_improvement">Feature Improvement</SelectItem>
                  <SelectItem value="bug_issue">Bug Issue</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="help-title">Title</Label>
              <Input
                id="help-title"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="help-desc">Description</Label>
              <Textarea
                id="help-desc"
                required
                rows={5}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Button type="button" variant="outline" asChild>
                <label className="cursor-pointer">
                  <CloudUpload className="mr-2 h-4 w-4" />
                  Upload Photo (Optional)
                  <input type="file" hidden accept="image/*" onChange={handlePhotoChange} />
                </label>
              </Button>
              {photo && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="truncate">{photo.name}</span>
                  <Button type="button" variant="ghost" size="icon" onClick={() => setPhoto(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            <Button type="submit" disabled={loading || !requestType || !title || !description}>
              {loading ? "Submitting..." : "Submit"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Help;
